<!--
	10 / FAQ (plan/04 §07) — shared between the homepage's inline six and the full
	set on /faq. `standalone` promotes the heading to the page's <h1> instead of
	leaving it as a section label.

	Two columns on wide screens: the heading sits beside the list rather than above
	it, so the questions start at the top of the viewport instead of a screen down.
-->
<script lang="ts">
	import { Section, FaqItem } from '$lib/components/ui';
	import { faqs, inlineFaqs } from '$lib/content/faq';
	import { nav, site } from '$lib/content/site';

	let {
		full = false,
		standalone = false
	}: {
		/** Full set (all of faq.ts) vs. just the six flagged `inline`. */
		full?: boolean;
		/** This is the whole page (/faq), not a homepage section. */
		standalone?: boolean;
	} = $props();

	const items = $derived(full ? faqs : inlineFaqs);
	const heading = nav.links.find((l) => l.href === '/#faq')?.label ?? 'FAQ';
</script>

<Section id="faq">
	<div class="faq-layout">
		<div class="faq-side">
			<p class="faq-eyebrow">
				<span class="faq-tick" aria-hidden="true"></span>
				<span class="faq-index">10</span>
				<span>FAQ</span>
			</p>
			{#if standalone}
				<h1 class="faq-h">{heading}</h1>
			{:else}
				<h2 class="faq-h">Questions, answered straight.</h2>
			{/if}
			<p class="faq-contact">
				Anything else: <a href="mailto:{site.contactEmail}">{site.contactEmail}</a>
			</p>
		</div>

		<div class="faq-list">
			{#each items as item (item.id)}
				<FaqItem question={item.q} level={standalone ? 2 : 3}>
					<p>{item.a}</p>
				</FaqItem>
			{/each}
		</div>
	</div>
</Section>

<style>
	.faq-layout {
		display: grid;
		gap: clamp(2rem, 4vw, 3rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.faq-layout {
			grid-template-columns: minmax(0, 4fr) minmax(0, 7fr);
			gap: 4rem;
		}
		.faq-side {
			position: sticky;
			top: 6rem;
		}
	}

	.faq-eyebrow {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-400);
		margin-bottom: 1.25rem;
	}
	.faq-tick {
		width: 6px;
		height: 6px;
		flex: none;
		background-color: var(--color-cut-600);
	}
	.faq-index {
		/* ink-400: the index is text (WCAG AA), not decoration. */
		color: var(--color-ink-400);
	}
	.faq-h {
		font-size: var(--text-h2);
		font-weight: 600;
		line-height: 1.04;
		letter-spacing: -0.025em;
		max-width: 16ch;
	}
	.faq-contact {
		margin-top: 1.5rem;
		color: var(--color-ink-400);
	}
	.faq-contact a {
		color: var(--color-cut-700);
		text-decoration: underline;
		text-underline-offset: 0.15em;
	}

	/* The list's first item needs a top rule; FaqItem only draws its bottom one. */
	.faq-list {
		border-top: 1px solid var(--color-line);
	}
</style>
